/**
 * Team — TanStack Query hooks поверх /api/v1/users + /clusters + /farms.
 *
 * Backend: users.py (GET /users, POST /users/invite, PATCH /users/members/{id},
 * DELETE /users/{member_id}, DELETE /users/invitations/{id}).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ClusterOption,
  FarmOption,
  Invitation,
  InvitePayload,
  MemberPatchPayload,
  UsersPage,
} from '@/pages/team/types';

export const teamKeys = {
  me: () => ['team', 'me'] as const,
  users: () => ['team', 'users'] as const,
  clusters: () => ['team', 'clusters'] as const,
  farms: () => ['team', 'farms'] as const,
};

/** Текущий пользователь + его роль в активном тенанте — для «Вы» и гейта. */
export interface TeamMe {
  user: { id: string; platform_role?: string | null };
  active_tenant?: { id: string } | null;
  memberships: Array<{ tenant: { id: string }; role: string }>;
}

export function useMe(): UseQueryResult<TeamMe, Error> {
  return useQuery({
    queryKey: teamKeys.me(),
    queryFn: () => api.get<TeamMe>('/api/v1/auth/me'),
    staleTime: 60_000,
  });
}

export function useTeam(): UseQueryResult<UsersPage, Error> {
  return useQuery({
    queryKey: teamKeys.users(),
    // AK-365: тянем ВСЕ статусы (active+blocked+archived); фильтрацию по
    // видимости делает клиент (тумблеры «показать заблокированных / архив»),
    // чтобы счётчики были точными без лишних запросов.
    queryFn: () =>
      api.get<UsersPage>(
        '/api/v1/users?include_blocked=true&include_archived=true',
      ),
    staleTime: 15_000,
  });
}

export function useClusters(): UseQueryResult<ClusterOption[], Error> {
  return useQuery({
    queryKey: teamKeys.clusters(),
    queryFn: () => api.get<ClusterOption[]>('/api/v1/clusters'),
    staleTime: 5 * 60_000,
  });
}

export function useFarms(): UseQueryResult<FarmOption[], Error> {
  return useQuery({
    queryKey: teamKeys.farms(),
    queryFn: () => api.get<FarmOption[]>('/api/v1/farms'),
    staleTime: 5 * 60_000,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvitePayload) =>
      api.post<Invitation>('/api/v1/users/invite', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.delete<void>(`/api/v1/users/invitations/${invitationId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      payload,
    }: {
      memberId: string;
      payload: MemberPatchPayload;
    }) => api.patch(`/api/v1/users/members/${memberId}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}

/** Блокировка (AK-365): POST /users/members/{id}/block (active→blocked, обратимо). */
export function useBlockMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.post<void>(`/api/v1/users/members/${memberId}/block`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}

/** Разблокировка (AK-365): POST /users/members/{id}/unblock (blocked→active). */
export function useUnblockMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.post<void>(`/api/v1/users/members/${memberId}/unblock`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}

/** Архивация (AK-365): POST /users/members/{id}/archive (active|blocked→archived, насовсем). */
export function useArchiveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.post<void>(`/api/v1/users/members/${memberId}/archive`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.users() });
    },
  });
}
